/**
 * Body of the notes-popover for a tree row.
 *
 * Renders the optional recipient controls section (role select + resolved-email
 * readout + enabled toggle) when the host node carries `recipientControls`,
 * the existing notes list (most-recent-first) with per-note delete, and the
 * "add note" textarea + submit. Pure presentational: every state change is
 * delegated to the host via callbacks. Used only inside `NotesPopover`.
 */
import { FC } from 'react';
import { Button, Checkbox, Divider, Input, Select, Typography, theme } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { Timestamp } from 'firebase/firestore';
import type { NodeExtraInformation } from '#types/System.RelationshipTree.types';
import FlexCol from '@/components/atoms/FlexCol';
import FlexBox from '@/components/atoms/FlexBox';

const { TextArea } = Input;
const { Text } = Typography;

type Note = NonNullable<NodeExtraInformation['notes']>[number];

type RecipientControls = {
  role?: 'to' | 'cc' | 'bcc';
  enabled?: boolean;
  resolvedEmail: string | null;
  onRoleChange: (role: 'to' | 'cc' | 'bcc') => void;
  onEnabledChange: (enabled: boolean) => void;
};

type NotesPopoverContentProps = {
  notes: NonNullable<NodeExtraInformation['notes']>;
  newNote: string;
  onNewNoteChange: (value: string) => void;
  onAddNote: () => void;
  onDeleteNote: (index: number) => void;
  formatTimestamp: (timestamp: Timestamp) => string;
  disabled?: boolean;
  recipientControls?: RecipientControls;
};

const roleOptions = [
  { value: 'to', label: 'To' },
  { value: 'cc', label: 'Cc' },
  { value: 'bcc', label: 'Bcc' },
];

const NotesPopoverContent: FC<NotesPopoverContentProps> = ({
  notes,
  newNote,
  onNewNoteChange,
  onAddNote,
  onDeleteNote,
  formatTimestamp,
  disabled = false,
  recipientControls,
}) => {
  const { token } = theme.useToken();
  const hasNotes = notes.length > 0;
  const hasRecipientControls = recipientControls !== undefined;

  return (
    <FlexCol style={{ width: token.controlHeight * 10 }}>
      {hasRecipientControls && (
        <FlexCol gap={5}>
          <Text type="secondary">Recipient settings</Text>
          <FlexCol gap={5}>
            <Text type="secondary">Role</Text>
            <Select
              aria-label="recipient-role-select"
              disabled={disabled}
              value={recipientControls.role}
              options={roleOptions}
              placeholder="Select role"
              onChange={(role) => recipientControls.onRoleChange(role)}
              style={{ width: '100%' }}
            />
          </FlexCol>
          <FlexCol gap={5}>
            <Text type="secondary">Resolved email</Text>
            {recipientControls.resolvedEmail ? (
              <Text aria-label="recipient-email-resolved" copyable>
                {recipientControls.resolvedEmail}
              </Text>
            ) : (
              <Text aria-label="recipient-email-resolved" type="secondary">
                No email found
              </Text>
            )}
          </FlexCol>
          <Checkbox
            aria-label="recipient-enabled-toggle"
            disabled={disabled}
            checked={recipientControls.enabled !== false}
            onChange={(e) => recipientControls.onEnabledChange(e.target.checked)}
          >
            Enabled
          </Checkbox>
        </FlexCol>
      )}
      {hasRecipientControls && <Divider style={{ margin: 0 }} />}
      {hasNotes && (
        <FlexCol>
          {[...notes].reverse().map((note: Note, index: number) => {
            const originalIndex = notes.length - 1 - index;
            const noteKey =
              note.timestamp instanceof Timestamp
                ? `${note.timestamp.toMillis()}:${note.userName}`
                : `${originalIndex}:${note.userName}`;
            return (
              <FlexCol key={noteKey} gap={0}>
                <FlexBox stretch>
                  <Text>{note.text}</Text>
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => onDeleteNote(originalIndex)}
                    disabled={disabled}
                    danger
                    aria-label={`delete-note-${originalIndex}`}
                  />
                </FlexBox>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {note.userName} •{' '}
                  {note.timestamp instanceof Timestamp
                    ? formatTimestamp(note.timestamp)
                    : ''}
                </Text>
                <Divider style={{ margin: 0 }} />
              </FlexCol>
            );
          })}
        </FlexCol>
      )}

      <FlexCol gap={5}>
        <TextArea
          value={newNote}
          onChange={(e) => onNewNoteChange(e.target.value)}
          placeholder="Type a note..."
          rows={3}
          disabled={disabled}
          aria-label="add-note-textarea"
        />
        <Button
          type="primary"
          onClick={onAddNote}
          disabled={disabled || !newNote.trim()}
          block
          aria-label="add-note-button"
        >
          Add Note
        </Button>
      </FlexCol>
    </FlexCol>
  );
};

export default NotesPopoverContent;
